import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { TvPartner, TvTournament } from '../hooks/useTvData';

interface Props {
  tournament: TvTournament | null;
  partners: TvPartner[];
}

const TIER_CONFIG: Record<string, {
  label: string;
  gradient: string;
  glow: string;
  badge: string;
  textColor: string;
  accent: string;
  rank: number;
}> = {
  'tournament host': { label: 'Tournament Host', gradient: 'from-emerald-900/80 to-emerald-950/60', glow: 'shadow-emerald-500/20', badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', textColor: 'text-emerald-300', accent: 'border-emerald-600/40', rank: 0 },
  'title sponsor':   { label: 'Title Sponsor',    gradient: 'from-cyan-900/80 to-cyan-950/60',     glow: 'shadow-cyan-500/20',    badge: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',       textColor: 'text-cyan-300',    accent: 'border-cyan-600/40',    rank: 1 },
  diamond:           { label: 'Diamond',           gradient: 'from-sky-900/80 to-sky-950/60',       glow: 'shadow-sky-500/20',     badge: 'bg-sky-500/20 text-sky-300 border-sky-500/40',           textColor: 'text-sky-300',     accent: 'border-sky-600/40',     rank: 2 },
  platinum:          { label: 'Platinum',          gradient: 'from-slate-700/80 to-slate-900/60',   glow: 'shadow-slate-400/20',   badge: 'bg-slate-400/20 text-slate-200 border-slate-400/40',     textColor: 'text-slate-200',   accent: 'border-slate-500/40',   rank: 3 },
  gold:              { label: 'Gold',              gradient: 'from-yellow-900/80 to-yellow-950/60', glow: 'shadow-yellow-500/20',  badge: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',  textColor: 'text-yellow-300',  accent: 'border-yellow-600/40',  rank: 4 },
  silver:            { label: 'Silver',            gradient: 'from-gray-700/80 to-gray-900/60',     glow: 'shadow-gray-400/20',    badge: 'bg-gray-400/20 text-gray-200 border-gray-400/40',         textColor: 'text-gray-200',    accent: 'border-gray-500/40',    rank: 5 },
  bronze:            { label: 'Bronze',            gradient: 'from-amber-900/80 to-amber-950/60',   glow: 'shadow-amber-600/20',   badge: 'bg-amber-600/20 text-amber-300 border-amber-600/40',     textColor: 'text-amber-300',   accent: 'border-amber-700/40',   rank: 6 },
};

function getTierConfig(tier: string) {
  const key = tier.toLowerCase();
  return TIER_CONFIG[key] ?? {
    label: tier,
    gradient: 'from-white/5 to-white/2',
    glow: 'shadow-white/5',
    badge: 'bg-white/10 text-white/60 border-white/20',
    textColor: 'text-white/60',
    accent: 'border-white/15',
    rank: 99,
  };
}

const FEATURE_INTERVAL = 8000;

function FeaturedCard({ partner }: { partner: TvPartner }) {
  const cfg = getTierConfig(partner.tier);
  const img = partner.image_url ?? partner.logo_url;

  return (
    <motion.div
      key={partner.id}
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.02 }}
      transition={{ duration: 0.7, ease: 'easeInOut' }}
      className={`relative flex flex-col h-full rounded-2xl lg:rounded-3xl border overflow-hidden bg-gradient-to-br ${cfg.gradient} ${cfg.accent} shadow-2xl ${cfg.glow}`}
    >
      {img && (
        <div className="absolute inset-0 overflow-hidden rounded-2xl lg:rounded-3xl">
          <img src={img} alt="" className="w-full h-full object-cover opacity-15 scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" />
        </div>
      )}

      <div className="relative flex-1 flex flex-col p-4 sm:p-6 lg:p-8 gap-3 sm:gap-5">
        <div className="flex items-start justify-between">
          <span className={`px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-sm font-bold uppercase tracking-widest border ${cfg.badge}`}>
            {cfg.label}
          </span>
        </div>

        <div className="flex items-center gap-3 sm:gap-5 mt-auto">
          {partner.logo_url && (
            <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-xl lg:rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center p-2 sm:p-3 flex-shrink-0 backdrop-blur-sm">
              <img src={partner.logo_url} alt={partner.company_name} className="w-full h-full object-contain" />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">{partner.company_name}</h3>
            {partner.website && (
              <p className={`text-sm lg:text-base mt-1 ${cfg.textColor}`}>{partner.website.replace(/^https?:\/\//, '')}</p>
            )}
          </div>
        </div>

        {partner.description && (
          <p className="text-white/60 text-sm lg:text-lg leading-relaxed line-clamp-3">
            {partner.description}
          </p>
        )}

        {partner.stats && partner.stats.length > 0 && (
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-2">
            {partner.stats.map((stat, i) => (
              <div key={i} className={`px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-lg sm:rounded-xl border backdrop-blur-sm ${cfg.accent} bg-white/5`}>
                <p className={`font-bold text-sm lg:text-base ${cfg.textColor}`}>{stat}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function SupporterLogo({ partner }: { partner: TvPartner }) {
  const cfg = getTierConfig(partner.tier);
  const logo = partner.logo_url ?? partner.image_url;
  return (
    <div className={`flex flex-col items-center gap-1.5 sm:gap-2 p-2 sm:p-4 rounded-xl sm:rounded-2xl border bg-white/3 hover:bg-white/8 transition-all ${cfg.accent}`}>
      <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center">
        {logo ? (
          <img src={logo} alt={partner.company_name} className="w-full h-full object-contain" />
        ) : (
          <span className={`text-xl sm:text-2xl font-bold ${cfg.textColor}`}>{partner.company_name.charAt(0)}</span>
        )}
      </div>
      <p className="text-white/60 text-[9px] sm:text-xs font-medium text-center leading-tight max-w-[64px] sm:max-w-[80px] truncate">
        {partner.company_name}
      </p>
    </div>
  );
}

export default function TvSponsorsSlide({ tournament, partners }: Props) {
  const [featuredIndex, setFeaturedIndex] = useState(0);

  const sorted = [...partners].sort((a, b) => {
    const ar = getTierConfig(a.tier).rank;
    const br = getTierConfig(b.tier).rank;
    return ar !== br ? ar - br : (a.company_name < b.company_name ? -1 : 1);
  });

  const topTiers = ['tournament host', 'title sponsor', 'diamond', 'platinum'];
  const featured = sorted.filter((p) => topTiers.includes(p.tier.toLowerCase()));
  const supporters = sorted.filter((p) => !topTiers.includes(p.tier.toLowerCase()));

  const featuredPartner = featured.length > 0 ? featured[featuredIndex % featured.length] : null;

  useEffect(() => {
    if (featured.length <= 1) return;
    const t = setInterval(() => setFeaturedIndex((i) => (i + 1) % featured.length), FEATURE_INTERVAL);
    return () => clearInterval(t);
  }, [featured.length]);

  if (partners.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-golf-gray text-white">
        <p className="text-white/30 text-xl sm:text-2xl">No partners to display</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-[#080e14] text-white overflow-hidden">
      <div
        className="relative flex items-center justify-between px-4 sm:px-8 lg:px-14 py-4 sm:py-5 lg:py-7 border-b border-white/8 flex-shrink-0 overflow-hidden"
        style={{ background: 'linear-gradient(120deg, #0f1e2e 0%, #0a1520 100%)' }}
      >
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}
        />
        <div className="relative min-w-0">
          <p className="text-golf-orange text-xs sm:text-sm font-bold uppercase tracking-[0.25em]">Official Partners</p>
          <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold font-display mt-0.5 sm:mt-1 truncate">
            {tournament?.name ?? 'Tournament'} Sponsors
          </h2>
        </div>
        <div className="relative text-right text-white/35 flex-shrink-0">
          <p className="text-xs sm:text-sm uppercase tracking-widest hidden sm:block">Proud to partner with</p>
          <p className="text-2xl sm:text-3xl font-bold text-white/60">{sorted.length}</p>
          <p className="text-xs sm:text-sm">organisations</p>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden gap-0 flex-col sm:flex-row">
        <div className="flex-1 p-3 sm:p-4 lg:p-6 flex flex-col gap-3 sm:gap-4 overflow-hidden">
          {featuredPartner && (
            <AnimatePresence mode="wait">
              <FeaturedCard key={featuredPartner.id} partner={featuredPartner} />
            </AnimatePresence>
          )}

          {featured.length > 1 && (
            <div className="flex items-center justify-center gap-2 flex-shrink-0">
              {featured.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setFeaturedIndex(i)}
                  className={`rounded-full transition-all duration-300 ${
                    i === featuredIndex % featured.length
                      ? 'w-8 h-2 bg-golf-orange'
                      : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {supporters.length > 0 && (
          <div className="sm:w-56 md:w-64 lg:w-80 flex-shrink-0 border-t sm:border-t-0 sm:border-l border-white/8 flex flex-col overflow-hidden max-h-40 sm:max-h-none">
            <div className="px-4 sm:px-6 py-2 sm:py-4 border-b border-white/8 flex-shrink-0">
              <p className="text-white/35 text-[10px] sm:text-xs font-bold uppercase tracking-widest">Supporting Partners</p>
            </div>
            <div className="flex-1 overflow-y-auto p-2 sm:p-4">
              <div className="grid grid-cols-4 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                {supporters.map((p) => (
                  <SupporterLogo key={p.id} partner={p} />
                ))}
              </div>
            </div>

            <div className="border-t border-white/8 px-4 sm:px-6 py-2 sm:py-4 flex-shrink-0 hidden sm:block">
              <p className="text-white/20 text-xs text-center leading-relaxed">
                Interested in partnering?<br />Contact us for sponsorship opportunities.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex-shrink-0 border-t border-white/8 overflow-hidden" style={{ height: 52 }}>
        <div className="relative h-full overflow-hidden bg-white/3">
          <motion.div
            className="flex items-center gap-8 sm:gap-10 px-6 sm:px-8 h-full"
            animate={{ x: [0, -(sorted.length * 160)] }}
            transition={{ duration: sorted.length * 4, repeat: Infinity, ease: 'linear', repeatType: 'loop' }}
            style={{ width: 'max-content' }}
          >
            {[...sorted, ...sorted].map((p, i) => {
              const logo = p.logo_url ?? p.image_url;
              const cfg = getTierConfig(p.tier);
              return (
                <div key={`${p.id}-${i}`} className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center">
                    {logo ? (
                      <img src={logo} alt={p.company_name} className="w-full h-full object-contain opacity-60" />
                    ) : (
                      <span className={`font-bold text-xs sm:text-sm ${cfg.textColor} opacity-60`}>{p.company_name.charAt(0)}</span>
                    )}
                  </div>
                  <span className="text-white/30 text-xs sm:text-sm font-medium">{p.company_name}</span>
                  <span className="text-white/10 ml-2 sm:ml-4">|</span>
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
